import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { latestResume } from "@/lib/api";
import { Upload, Sparkles, ArrowRight, FileText, ArrowLeft, Loader2 } from "lucide-react";
import ResumePage from "@/pages/app/ResumePage";
import ResumeWizard from "@/components/wishire/ResumeWizard";

/* The Resume module entry: first ask what the student has, then the journey.
     "I have a resume"   → upload, score, fix (ResumePage)
     "Build a new one"   → prefilled details → quick chat → template → build
   ?tab=score / ?tab=build deep-link straight into either path (legacy links). */

export default function ResumeJourneyPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab");
  const [mode, setMode] = useState(tab === "build" ? "build" : tab === "score" ? "upload" : "choose");
  const [latest, setLatest] = useState(undefined);

  useEffect(() => { latestResume().then(({ data }) => setLatest(data?.result ? data : null)).catch(() => setLatest(null)); }, []);
  useEffect(() => { if (tab === "build") setMode("build"); else if (tab === "score") setMode("upload"); }, [tab]);

  const go = (m) => { setMode(m); setParams(m === "build" ? { tab: "build" } : m === "upload" ? { tab: "score" } : {}, { replace: true }); };

  if (mode === "upload") return (
    <div>
      <div className="container-x pt-6"><button onClick={() => go("choose")} className="text-sm text-stone-500 hover:text-stone-900 inline-flex items-center gap-1.5"><ArrowLeft size={14} /> Resume options</button></div>
      <ResumePage />
    </div>
  );

  if (mode === "build") return (
    <div className="container-x py-10 md:py-14">
      <div className="mb-8">
        <span className="overline">Build on Wishire</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">Let's build your resume <span className="italic text-stone-500">together.</span></h1>
        <p className="mt-3 text-stone-500 max-w-xl">We've pre-filled what we know. A few quick questions, pick a look, and the AI writes it — from your facts only.</p>
      </div>
      <ResumeWizard onExit={() => go("choose")} />
    </div>
  );

  const score = latest?.result?.overall_score;
  return (
    <div className="container-x py-10 md:py-14" data-testid="resume-choose">
      <div className="mb-10 max-w-2xl">
        <span className="overline">Resume</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">Your resume is 25% of your Wishire Score. <span className="italic text-stone-500">Let's get it right.</span></h1>
        <p className="mt-3 text-stone-600">Do you already have one, or are we starting fresh?</p>
      </div>

      {latest === undefined ? (
        <p className="text-stone-500 inline-flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> Checking for an existing resume…</p>
      ) : latest ? (
        <div className="mb-6 rounded-3xl border border-stone-200 bg-white p-6 flex flex-wrap items-center justify-between gap-4" data-testid="resume-existing">
          <div className="flex items-center gap-4">
            <span className="w-14 h-14 rounded-2xl bg-stone-900 text-amber-300 font-display text-2xl flex items-center justify-center">{score ?? "—"}</span>
            <div><p className="font-display text-xl">You have a scored resume</p><p className="text-sm text-stone-500">Uploaded {latest.created_at ? new Date(latest.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "earlier"} · score {score}/100</p></div>
          </div>
          <button onClick={() => go("upload")} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-stone-900 text-stone-50 text-sm font-medium hover:bg-[#C2410C]">See score & fixes <ArrowRight size={14} /></button>
        </div>
      ) : null}

      <div className="grid md:grid-cols-2 gap-5">
        <button onClick={() => go("upload")} data-testid="resume-choose-upload" className="group text-left rounded-3xl border-2 border-stone-200 bg-white p-7 hover:border-stone-900 transition-colors">
          <span className="inline-flex p-3 rounded-2xl bg-stone-100 text-stone-800"><Upload size={22} /></span>
          <h2 className="font-display text-2xl mt-5">I have a resume</h2>
          <p className="text-sm text-stone-600 mt-2 leading-relaxed">Upload your PDF or Word file. We score it the way a recruiter's ATS would, show exactly what to fix, and rewrite weak lines for you.</p>
          <p className="mt-5 text-sm font-medium text-stone-900 group-hover:text-[#C2410C] inline-flex items-center gap-1">Upload & score <ArrowRight size={14} /></p>
        </button>
        <button onClick={() => go("build")} data-testid="resume-choose-build" className="group text-left rounded-3xl border-2 border-[#C2410C]/30 bg-amber-50/50 p-7 hover:border-[#C2410C] transition-colors">
          <span className="inline-flex p-3 rounded-2xl bg-[#C2410C] text-white"><Sparkles size={22} /></span>
          <h2 className="font-display text-2xl mt-5">Build a new one</h2>
          <p className="text-sm text-stone-600 mt-2 leading-relaxed">No resume yet, or yours is from a template you don't love? We pre-fill your details, ask five quick questions in chat, and write a clean fresher resume you can download.</p>
          <p className="mt-5 text-sm font-medium text-stone-900 group-hover:text-[#C2410C] inline-flex items-center gap-1">Start building <ArrowRight size={14} /></p>
        </button>
      </div>
      <p className="mt-6 text-xs text-stone-400 inline-flex items-center gap-1.5"><FileText size={12} /> Built resumes can be uploaded back here for a score — the loop that lifts your Wishire Score.</p>
    </div>
  );
}
